import React, {
    createContext,
    useContext,
    useEffect,
    useState,
} from "react";

import { api } from "../lib/api";

/*
 * ============================================================
 * MKTRANSFE — GLOBAL DYNAMIC DESIGN CONTEXT
 * ============================================================
 *
 * - Chaje published design pou tout itilizatè.
 * - Chaje draft design lè Admin mete Preview Mode.
 * - Aplike Theme kòm CSS Variables sou document.documentElement.
 * - Si Design API a tonbe, li kenbe defaults yo.
 * - Pa manyen okenn API/provider sèvis finansye yo.
 */


// ============================================================
// DEFAULT DESIGN
// ============================================================

export const DESIGN_DEFAULTS = {
    authAppearance: {
        login: { heroImageUrl: "", mobileHeroImageUrl: "", backgroundImageUrl: "", title: "Byenveni ankò 👋", subtitle: "Konekte ak kont MKTRANSFE ou pou kontinye jere sèvis ou yo.", imageEnabled: true },
        register: { heroImageUrl: "", mobileHeroImageUrl: "", backgroundImageUrl: "", title: "Kreye kont ou", subtitle: "Antre enfòmasyon yo pou kreye kont MKTRANSFE ou epi pwofite sèvis nou yo.", imageEnabled: true },
        showTrustBadges: true, slogan: "Plis pase yon sèvis, yon konfyans."
    },
    receipts: {
        activeTemplate: "RECEIPT_01",
        autoOpen: true,
        presentationDelayMs: 420,
        animationDurationMs: 780,
        showPreparingState: true,
    },
    theme: {
        mode: "dark",

        colors: {
            primary: "#22d3ee",
            accent: "#3b82f6",

            background: "#020812",

            surface: "rgba(255,255,255,0.05)",
            surfaceStrong: "#081522",

            success: "#34d399",
            warning: "#fbbf24",
            danger: "#f87171",

            textPrimary: "#F8FAFC",
            textSecondary: "#CBD5E1",
            textMuted: "#94A3B8",

            border: "#263548",

        },

        borderRadius: "16px",
        cardRadius: "24px",

        fontFamily: "system",

        glassEffect: true,
        animations: true,
        compactMode: false,
    },

    branding: {
        appName: "MKTRANSFE",
        shortName: "MK",

        slogan: "Voye lajan bay fanmi w rapid fasil",

        logoUrl: "",
        logoDarkUrl: "",
        faviconUrl: "",

        defaultLanguage: "ht",

        supportedLanguages: [
            "ht",
            "fr",
            "es",
            "en",
        ],
    },

    layout: {
        dashboards: {},
    },

    menu: {
        items: [],
    },

    pages: {
        pages: [],
    },

    content: {
        strings: {},
    },

    forms: {
        forms: [],
    },

    features: {
        services: {},
        ui: {},
    },

    services: {
        transferMethods: {},
    },

    cards: {
        providers: {},
        pricing: {},
        security: {},
    },

    fees: {},

    limits: {},

    maintenance: {
        enabled: false,
    },

    announcements: {
        items: [],
    },

    support: {},

    roles: {},
};


// ============================================================
// CONTEXT
// ============================================================

const DesignContext = createContext({
    design: DESIGN_DEFAULTS,

    loading: true,

    previewMode: false,

    reload: () => {},

    setPreviewMode: () => {},

    featureEnabled: () => true,

    transferMethodEnabled: () => true,
});


// ============================================================
// HOOK
// ============================================================

export function useDesign() {
    return useContext(DesignContext);
}


// ============================================================
// HELPERS
// ============================================================

function deepMerge(base, override) {
    if (
        typeof base !== "object" ||
        base === null ||
        Array.isArray(base)
    ) {
        return override ?? base;
    }

    const output = {
        ...base,
    };

    Object.entries(override || {}).forEach(([key, value]) => {
        if (
            value &&
            typeof value === "object" &&
            !Array.isArray(value) &&
            typeof base?.[key] === "object" &&
            base?.[key] !== null &&
            !Array.isArray(base?.[key])
        ) {
            output[key] = deepMerge(
                base[key],
                value
            );
        } else {
            output[key] = value;
        }
    });

    return output;
}


function resolveFontFamily(fontFamily) {
    const value = String(
        fontFamily || ""
    )
        .trim()
        .toLowerCase();

    if (
        !value ||
        value === "system"
    ) {
        return `
            Inter,
            system-ui,
            -apple-system,
            BlinkMacSystemFont,
            "Segoe UI",
            sans-serif
        `;
    }

    return fontFamily;
}


// ============================================================
// APPLY GLOBAL CSS VARIABLES
// ============================================================

function applyThemeVariables(theme) {
    if (
        typeof document === "undefined"
    ) {
        return;
    }

    const root =
        document.documentElement;

    const colors =
        theme?.colors || {};

    const variables = {
        "--dm-primary":
            colors.primary,

        "--dm-accent":
            colors.accent,

        "--dm-background":
            colors.background,

        "--dm-surface":
            colors.surface,

        "--dm-surface-strong":
            colors.surfaceStrong,

        "--dm-success":
            colors.success,

        "--dm-warning":
            colors.warning,

        "--dm-danger":
            colors.danger,

        "--dm-text-primary":
            colors.textPrimary ||
            "#F8FAFC",

        "--dm-text-secondary":
            colors.textSecondary ||
            "#CBD5E1",

        "--dm-text-muted":
            colors.textMuted ||
            "#94A3B8",

        "--dm-border":
            colors.border ||
            "#263548",

        "--dm-radius":
            theme?.borderRadius,

        "--dm-card-radius":
            theme?.cardRadius,

        "--dm-font-family":
            resolveFontFamily(
                theme?.fontFamily
            ),
    };

    Object.entries(
        variables
    ).forEach(
        ([key, value]) => {
            if (
                value !== undefined &&
                value !== null &&
                value !== ""
            ) {
                root.style.setProperty(
                    key,
                    String(value)
                );
            }
        }
    );

    // ========================================================
    // DATA ATTRIBUTES
    // ========================================================

    root.dataset.themeMode =
        theme?.mode || "dark";

    root.dataset.glassEffect =
        theme?.glassEffect === false
            ? "off"
            : "on";

    root.dataset.animations =
        theme?.animations === false
            ? "off"
            : "on";

    root.dataset.compact =
        theme?.compactMode
            ? "on"
            : "off";
}


// ============================================================
// BRANDING
// ============================================================

function applyBranding(
    branding
) {
    if (
        typeof document === "undefined"
    ) {
        return;
    }

    const appName =
        branding?.appName ||
        "MKTRANSFE";

    document.title =
        appName;

    if (
        branding?.faviconUrl
    ) {
        let favicon =
            document.querySelector(
                'link[rel="icon"]'
            );

        if (!favicon) {
            favicon =
                document.createElement(
                    "link"
                );

            favicon.rel =
                "icon";

            document.head.appendChild(
                favicon
            );
        }

        favicon.href =
            branding.faviconUrl;
    }
}


// ============================================================
// PROVIDER
// ============================================================

export function DesignProvider({
    children,
}) {
    const [
        design,
        setDesign,
    ] = useState(
        DESIGN_DEFAULTS
    );

    const [
        loading,
        setLoading,
    ] = useState(true);

    const [
        previewMode,
        setPreviewModeState,
    ] = useState(
        sessionStorage.getItem(
            "mk_design_preview"
        ) === "1"
    );


    // ========================================================
    // LOAD DESIGN
    // ========================================================

    const load = async (
        preview = previewMode
    ) => {
        setLoading(true);

        try {
            const url =
                preview
                    ? "/admin/design/preview"
                    : "/design/published";

            const {
                data,
            } = await api.get(
                url
            );

            const merged =
                deepMerge(
                    DESIGN_DEFAULTS,
                    data || {}
                );

            setDesign(
                merged
            );

            applyThemeVariables(
                merged.theme
            );

            applyBranding(
                merged.branding
            );
        } catch (
            error
        ) {
            console.warn(
                "Design Manager pa disponib. Default theme ap itilize.",
                error
            );

            setDesign(
                DESIGN_DEFAULTS
            );

            applyThemeVariables(
                DESIGN_DEFAULTS.theme
            );

            applyBranding(
                DESIGN_DEFAULTS.branding
            );
        } finally {
            setLoading(
                false
            );
        }
    };


    // ========================================================
    // INITIAL LOAD
    // ========================================================

    useEffect(() => {
        load();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    // ========================================================
    // PREVIEW MODE
    // ========================================================

    const setPreviewMode = (
        enabled
    ) => {
        if (enabled) {
            sessionStorage.setItem(
                "mk_design_preview",
                "1"
            );
        } else {
            sessionStorage.removeItem(
                "mk_design_preview"
            );
        }

        setPreviewModeState(
            enabled
        );

        load(
            enabled
        );
    };


    // ========================================================
    // FEATURE HELPERS
    // ========================================================

    const featureEnabled = (
        featureKey,
        fallback = true
    ) => {
        const feature =
            design?.features
                ?.services?.[
                featureKey
            ];

        if (
            feature ===
            undefined
        ) {
            return fallback;
        }

        if (
            typeof feature ===
            "boolean"
        ) {
            return feature;
        }

        return (
            feature?.enabled !==
            false
        );
    };


    const transferMethodEnabled =
        (
            serviceKey,
            fallback = true
        ) => {
            const key =
                String(
                    serviceKey || ""
                )
                    .trim()
                    .toUpperCase();

            const service =
                design?.services
                    ?.transferMethods?.[
                    key
                ];

            if (
                service ===
                undefined
            ) {
                return fallback;
            }

            return (
                service?.enabled !==
                false
            );
        };


    // ========================================================
    // CONTEXT VALUE
    // ========================================================

    const contextValue = {
        design,

        loading,

        previewMode,

        reload: load,

        setPreviewMode,

        featureEnabled,

        transferMethodEnabled,
    };


    return (
        <DesignContext.Provider
            value={
                contextValue
            }
        >
            {previewMode && (
                <div
                    data-testid="design-preview-banner"
                    className="
                        fixed
                        bottom-3
                        left-1/2
                        -translate-x-1/2
                        z-[999]
                        flex
                        items-center
                        gap-3
                        bg-amber-500/90
                        text-black
                        text-xs
                        font-bold
                        px-4
                        py-2
                        rounded-full
                        shadow-xl
                    "
                >
                    MÒD PREVIEW
                    (draft) —
                    chanjman yo
                    poko pibliye

                    <button
                        type="button"
                        data-testid="exit-preview-btn"
                        onClick={() =>
                            setPreviewMode(
                                false
                            )
                        }
                        className="
                            bg-black/80
                            text-white
                            px-2
                            py-0.5
                            rounded-full
                        "
                    >
                        Soti
                    </button>
                </div>
            )}

            {children}
        </DesignContext.Provider>
    );
}